// Sign-in, link verification and invite acceptance pages.
import type { InvitePreview, MeResponse, WorkspaceInfo } from '../../shared/api.ts';
import { get, post, takeNextPath } from '../api.ts';
import { visitSource } from '../analytics.ts';
import { h } from '../util/dom.ts';
import { busy, centered, field, formError, mount, ROLE_LABELS, setTitle, termsNote } from './page.ts';

const isLocal = ['localhost', '127.0.0.1', '[::1]'].includes(location.hostname);

export function loginPage(): void {
  const signup = new URLSearchParams(location.search).has('signup');
  setTitle(signup ? 'Sign up' : 'Sign in');
  const email = h('input', { type: 'email', required: '', autocomplete: 'email', placeholder: 'you@company.com' });
  const submit = h('button', { type: 'submit', class: 'btn primary' }, [signup ? 'Email me a link to sign up' : 'Email me a sign-in link']);
  const error = formError();
  // Signing in with a new email creates an account, so both say what continuing agrees to.
  const form = h('form', { class: 'stack' }, [field('Email', email), error, submit, termsNote()]);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.clear();
    void busy(submit, async () => {
      try {
        await post('/api/auth/request-link', { email: email.value, source: visitSource() }, { redirectOn401: false });
        sent(email.value.trim());
      } catch (err) {
        error.show(err);
      }
    });
  });

  const other = signup
    ? h('p', { class: 'field-hint' }, ['Already have an account? ', h('a', { href: '/login' }, ['Sign in'])])
    : h('p', { class: 'field-hint' }, ['New here? ', h('a', { href: '/login?signup=1' }, ['Create a free account']), ', or ', h('a', { href: '/local/new' }, ['start drawing without one']), '.']);
  mount(centered(
    h('h1', {}, [signup ? 'Create your free account' : 'Sign in']),
    h('p', { class: 'muted' }, [signup
      ? "We'll email you a link. No password needed. Boards you've started in this browser can come with you."
      : "We'll email you a link. No password needed."]),
    form,
    other,
  ));
  email.focus();
}

function sent(email: string): void {
  const again = h('button', { type: 'button', class: 'link-btn' }, ['Use a different email']);
  again.addEventListener('click', loginPage);
  mount(centered(
    h('h1', {}, ['Check your email']),
    h('p', {}, ['A link is on its way to ', h('strong', {}, [email]), '. It works once and expires in 15 minutes.']),
    ...(isLocal ? [h('p', { class: 'field-hint' }, ['Running locally: the email (and its link) is printed in the server console.'])] : []),
    again,
  ));
}

export function verifyPage(): void {
  setTitle('Sign in');
  const token = new URLSearchParams(location.search).get('token');
  if (!token) {
    mount(centered(h('h1', {}, ['Link incomplete']), h('p', { class: 'muted' }, ['This sign-in link is missing its token.']),
      h('a', { class: 'btn', href: '/login' }, ['Request a new link'])));
    return;
  }
  // The token is only spent when the button is pressed, so link scanners that
  // prefetch URLs in email can't use it up (spec §8).
  const go = h('button', { type: 'button', class: 'btn primary' }, ['Continue signing in']);
  const error = formError();
  go.addEventListener('click', () => {
    error.clear();
    void busy(go, async () => {
      try {
        await post('/api/auth/verify', { token }, { redirectOn401: false });
        location.replace(takeNextPath());
      } catch (err) {
        error.show(err);
        go.replaceWith(h('a', { class: 'btn', href: '/login' }, ['Request a new link']));
      }
    });
  });
  mount(centered(h('h1', {}, ['Welcome']), h('p', { class: 'muted' }, ['Press continue to finish signing in.']), error, go));
  go.focus();
}

export async function invitePage(token: string): Promise<void> {
  setTitle('Invitation');
  const [preview, me] = await Promise.all([
    get<InvitePreview>(`/api/invites/${encodeURIComponent(token)}`, { redirectOn401: false }),
    get<MeResponse>('/api/me', { redirectOn401: false }).catch(() => null),
  ]);
  if (!preview.valid) {
    mount(centered(
      h('h1', {}, ['Invitation unavailable']),
      h('p', { class: 'muted' }, ['This invitation is invalid, has expired, or was already used. Ask for a new one.']),
      h('a', { class: 'btn', href: me ? '/' : '/login' }, [me ? 'Go to your boards' : 'Sign in']),
    ));
    return;
  }

  const isThem = me?.user.email === preview.email;
  const name = h('input', { type: 'text', autocomplete: 'name', maxlength: '80', placeholder: preview.email.split('@')[0]! });
  if (isThem) name.value = me!.user.name;
  const accept = h('button', { type: 'submit', class: 'btn primary' }, ['Accept invitation']);
  const error = formError();
  const form = h('form', { class: 'stack' }, [
    ...(isThem ? [] : [field('Your name', name, `You'll join as ${preview.email}.`)]),
    ...(me && !isThem ? [h('p', { class: 'field-hint' }, [`You're signed in as ${me.user.email}; accepting signs you in as ${preview.email}.`])] : []),
    error, accept,
    ...(isThem ? [] : [termsNote()]),
  ]);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.clear();
    void busy(accept, async () => {
      try {
        const res = await post<{ workspace: WorkspaceInfo }>('/api/invites/accept', { token, name: name.value }, { redirectOn401: false });
        location.replace(`/w/${res.workspace.slug}`);
      } catch (err) {
        error.show(err);
      }
    });
  });
  mount(centered(
    h('h1', {}, [`Join ${preview.workspaceName}`]),
    h('p', {}, [h('strong', {}, [preview.inviterName]), ` invited you to ${preview.workspaceName} as ${ROLE_LABELS[preview.role]!.toLowerCase()}.`]),
    form,
  ));
}
